import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getAllSegments,
  addSegment,
  updateSegment,
  deleteSegment,
} from "../../../services/segmentDossierServices";
import {
  addFichier,
  updateFichier,
  deleteFichier,
} from "../../../services/fichierDossierServices";

const SettingsDossierInscription = () => {
  const [segments, setSegments] = useState([]);
  const [newSegment, setNewSegment] = useState({
    segment_name: "",
    segment_content: "",
    ordre_segment: 0,
  });
  const [fileForms, setFileForms] = useState({});
  const [fileToEditId, setFileToEditId] = useState({}); // par segment

  const fetchSegments = async () => {
    const data = await getAllSegments();
    setSegments(data);
    // Initialise les formulaires fichiers pour chaque segment
    const initialForms = {};
    data.forEach((segment) => {
      initialForms[segment.id] = {
        nom_fichier: "",
        lien_fichier: "",
        detail_fichier: "",
      };
    });
    setFileForms(initialForms);
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  const handleAddSegment = async () => {
    const created = await addSegment(newSegment);
    if (created) {
      setNewSegment({
        segment_name: "",
        segment_content: "",
        ordre_segment: 0,
      });
      fetchSegments();
    }
  };

  const handleUpdateSegment = async (id, updatedSegment) => {
    await updateSegment(id, updatedSegment);
    fetchSegments();
  };

  const handleDeleteSegment = async (id) => {
    if (window.confirm("Supprimer ce segment ?")) {
      await deleteSegment(id);
      fetchSegments();
    }
  };

  const handleFileChange = (segmentId, field, value) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: {
        ...prev[segmentId],
        [field]: value,
      },
    }));
  };

  const handleAddFile = async (segmentId) => {
    const fileData = fileForms[segmentId];
    if (fileData.nom_fichier && fileData.lien_fichier) {
      await addFichier({
        segment_id: segmentId,
        ...fileData,
      });
      fetchSegments();
    }
  };

  const handleEditFile = async (file, segmentId) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: {
        nom_fichier: file.nom_fichier,
        lien_fichier: file.lien_fichier,
        detail_fichier: file.detail_fichier,
        id: file.id,
      },
    }));
    setFileToEditId((prev) => ({
      ...prev,
      [segmentId]: file.id,
    }));
  };

  const handleUpdateFile = async (file, segmentId) => {
    await updateFichier(file.id, file);
    fetchSegments();
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm("Supprimer ce fichier ?")) {
      await deleteFichier(fileId);
      fetchSegments();
    }
  };

  return (
    <div className="w-full max-w-[1000px] mx-auto font-montserrat">
      <h3 className="text-xl font-bold text-center mb-6">
        Gestion des Segments du Dossier d’Inscription
      </h3>

      {/* Formulaire de création de segment */}
      <div className="border border-dashed border-gray-400 rounded p-4 mb-6 bg-white shadow">
        <h4 className="text-lg font-semibold mb-2">➕ Nouveau segment</h4>
        <input
          type="text"
          className="w-full border rounded p-2 mb-2"
          placeholder="Nom du segment"
          value={newSegment.segment_name}
          onChange={(e) =>
            setNewSegment({ ...newSegment, segment_name: e.target.value })
          }
        />
        <input
          type="number"
          className="w-full border rounded p-2 mb-2"
          placeholder="Ordre"
          value={newSegment.ordre_segment}
          onChange={(e) =>
            setNewSegment({
              ...newSegment,
              ordre_segment: parseInt(e.target.value),
            })
          }
        />
        <ReactQuill
          theme="snow"
          value={newSegment.segment_content}
          onChange={(value) =>
            setNewSegment({ ...newSegment, segment_content: value })
          }
        />
        <button
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          onClick={handleAddSegment}
        >
          Ajouter ce segment
        </button>
      </div>

      {/* Segments existants */}
      {segments.map((segment) => (
        <div
          key={segment.id}
          className="border border-gray-300 rounded-lg mb-6 p-4 bg-white shadow"
        >
          <div className="flex gap-4 mb-2">
            <input
              type="text"
              className="w-full font-semibold border p-2"
              value={segment.segment_name}
              onChange={(e) =>
                handleUpdateSegment(segment.id, {
                  ...segment,
                  segment_name: e.target.value,
                })
              }
              onBlur={(e) =>
                handleUpdateSegment(segment.id, {
                  ...segment,
                  segment_name: e.target.value,
                })
              }
            />
            <input
              type="number"
              className="w-[120px] border p-2"
              value={segment.ordre_segment}
              onChange={(e) =>
                handleUpdateSegment(segment.id, {
                  ...segment,
                  ordre_segment: parseInt(e.target.value),
                })
              }
            />
          </div>

          <ReactQuill
            theme="snow"
            value={segment.segment_content}
            onChange={(value) =>
              handleUpdateSegment(segment.id, {
                ...segment,
                segment_content: value,
              })
            }
          />

          <button
            className="mt-2 text-red-600 text-sm underline"
            onClick={() => handleDeleteSegment(segment.id)}
          >
            Supprimer ce segment
          </button>

          <hr className="my-4" />

          <h5 className="font-semibold mb-2">📂 Fichiers liés :</h5>
          <ul className="list-disc pl-5 mb-2">
            {segment.fichiers?.map((file) => (
              <li key={file.id} className="mb-1 flex flex-col">
                <div>
                  <a
                    href={file.lien_fichier}
                    className="text-blue-600 underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {file.nom_fichier}
                  </a>{" "}
                  – <em>{file.detail_fichier}</em>
                </div>
                <div className="flex gap-2 text-sm mt-1">
                  <button
                    className="text-green-600 underline"
                    onClick={() => handleEditFile(file, segment.id)}
                  >
                    Modifier
                  </button>
                  <button
                    className="text-red-600 underline"
                    onClick={() => handleDeleteFile(file.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {/* Formulaire fichier */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <input
              type="text"
              className="border p-2"
              placeholder="Nom du fichier"
              value={fileForms[segment.id]?.nom_fichier || ""}
              onChange={(e) =>
                handleFileChange(segment.id, "nom_fichier", e.target.value)
              }
            />
            <input
              type="text"
              className="border p-2"
              placeholder="Lien du fichier"
              value={fileForms[segment.id]?.lien_fichier || ""}
              onChange={(e) =>
                handleFileChange(segment.id, "lien_fichier", e.target.value)
              }
            />
            <input
              type="text"
              className="border p-2"
              placeholder="Détail (optionnel)"
              value={fileForms[segment.id]?.detail_fichier || ""}
              onChange={(e) =>
                handleFileChange(segment.id, "detail_fichier", e.target.value)
              }
            />
          </div>

          <div className="flex justify-end">
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
              onClick={() => {
                if (fileToEditId[segment.id]) {
                  handleUpdateFile(
                    {
                      ...fileForms[segment.id],
                      segment_id: segment.id,
                      id: fileToEditId[segment.id],
                    },
                    segment.id
                  );
                  setFileToEditId((prev) => ({ ...prev, [segment.id]: null }));
                  setFileForms((prev) => ({
                    ...prev,
                    [segment.id]: {
                      nom_fichier: "",
                      lien_fichier: "",
                      detail_fichier: "",
                    },
                  }));
                } else {
                  handleAddFile(segment.id);
                }
              }}
            >
              {fileToEditId[segment.id]
                ? "Modifier ce fichier"
                : "Ajouter ce fichier"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SettingsDossierInscription;
